import { AnyAction } from 'redux';
import { App } from '@ps20-frontend/frontend-models';
import { GlobalActions, ProduktDeleted } from '@ps20-frontend/shop-integration-redux';
import { convertDateFormatFromShortIsoToLocalState } from '@ps20-frontend/utils';
import { INITIAL_STATE } from '../initialState';
import { canAddPosition } from '../models/positionCardinality';
import { TgvSubprodukt } from '../models/tgvSubprodukt';
import { createTier, MAX_TIERE, withFormPatch, withTierart } from '../models/tier';
import { withThhLimit } from '../models/thhLimit';
import { derivePositions, isThhAvailable, withoutThhCoverage } from '../models/tierPositions';
import { TgvPosition, TgvTier, TiergruppeState } from '../models/tiergruppe.model';
import {
  AngebotsIdAssignedPayload,
  IllnessToggledPayload,
  PositionAddedPayload,
  TgvAction,
  TgvActions,
  TierartSelectedPayload,
  TierFormChangedPayload,
  UmfangChangedPayload,
  VorschaedenChangedPayload,
} from '../tgvActions';

export const withTiere = (sectionState: TiergruppeState, tiere: TgvTier[]): TiergruppeState => {
  const limited = withThhLimit(tiere);
  return { ...sectionState, tiere: limited, positions: derivePositions(limited) };
};

export const withTkvOnly = (sectionState: TiergruppeState): TiergruppeState =>
  withTiere(sectionState, withoutThhCoverage(sectionState.tiere));

const withUpdatedTier = (
  sectionState: TiergruppeState,
  cardId: string,
  update: (tier: TgvTier) => TgvTier,
): TiergruppeState => {
  if (!sectionState.tiere.some(tier => tier.cardId === cardId)) {
    return sectionState;
  }
  return withTiere(
    sectionState,
    sectionState.tiere.map(tier => (tier.cardId === cardId ? update(tier) : tier)),
  );
};

const withAddedPosition = (
  sectionState: TiergruppeState,
  { subprodukt, positionId }: PositionAddedPayload,
): TiergruppeState => {
  const { positions } = sectionState;
  if (!canAddPosition(positions, subprodukt)) {
    return sectionState;
  }
  const position: TgvPosition = { positionId };
  if (subprodukt === TgvSubprodukt.THH_HUND) {
    return { ...sectionState, positions: { ...positions, thhHund: position } };
  }
  if (subprodukt === TgvSubprodukt.THH_PFERD) {
    return { ...sectionState, positions: { ...positions, thhPferd: position } };
  }
  return {
    ...sectionState,
    positions: { ...positions, tkvV2: [...positions.tkvV2, position] },
  };
};

const isListedTakeOverKey =
  ({ angebotsIds, thhAngelegt }: Pick<TiergruppeState, 'angebotsIds' | 'thhAngelegt'>) =>
  (key: string): boolean =>
    Object.hasOwn(angebotsIds, key) ||
    (key === App.THH_HUND && thhAngelegt.thhHund) ||
    (key === App.THH_PFERD && thhAngelegt.thhPferd);

const withoutChildren = (
  sectionState: TiergruppeState,
  removedIds: Set<string>,
  removesThhHund: boolean,
  removesThhPferd: boolean,
): TiergruppeState => {
  const angebotsIds = Object.fromEntries(
    Object.entries(sectionState.angebotsIds).filter(([, id]) => !removedIds.has(id)),
  );
  const thhAngelegt = {
    thhHund: sectionState.thhAngelegt.thhHund && !removesThhHund,
    thhPferd: sectionState.thhAngelegt.thhPferd && !removesThhPferd,
  };
  return {
    ...sectionState,
    angebotsIds,
    thhAngelegt,
    uebernommen: (sectionState.uebernommen ?? []).filter(
      isListedTakeOverKey({ angebotsIds, thhAngelegt }),
    ),
    deferredDeletion: (sectionState.deferredDeletion ?? []).filter(
      key =>
        !removedIds.has(key) &&
        !(key === App.THH_HUND && removesThhHund) &&
        !(key === App.THH_PFERD && removesThhPferd),
    ),
  };
};

export const tiergruppeReducer = function (
  sectionState: TiergruppeState = INITIAL_STATE.tiergruppe,
  action: AnyAction,
): TiergruppeState {
  switch (action.type) {
    case TgvActions.TGV_SUBPRODUKT_SELECTED:
      return { ...sectionState, selectedSubprodukt: action.payload };
    case TgvActions.TGV_POSITION_ADDED:
      return withAddedPosition(
        sectionState,
        (action as TgvAction<PositionAddedPayload>).payload,
      );
    case TgvActions.TGV_TIERART_SELECTED: {
      const { cardId, tierart } = (action as TgvAction<TierartSelectedPayload>).payload;
      return withUpdatedTier(sectionState, cardId, tier => withTierart(tier, tierart));
    }
    case TgvActions.TGV_UMFANG_CHANGED: {
      const { cardId, umfang } = (action as TgvAction<UmfangChangedPayload>).payload;
      return withUpdatedTier(sectionState, cardId, tier => ({
        ...tier,
        umfang: { ...umfang, thh: umfang.thh && isThhAvailable(tier.tierart) },
      }));
    }
    case TgvActions.TGV_TIER_FORM_CHANGED: {
      const { cardId, patch } = (action as TgvAction<TierFormChangedPayload>).payload;
      return withUpdatedTier(sectionState, cardId, tier => withFormPatch(tier, patch));
    }
    case TgvActions.TGV_ILLNESS_TOGGLED: {
      const { cardId, illness, checked } = (action as TgvAction<IllnessToggledPayload>).payload;
      return withUpdatedTier(sectionState, cardId, tier => ({
        ...tier,
        selectedIllnesses: checked
          ? [...tier.selectedIllnesses.filter(it => it.name !== illness.name), illness]
          : tier.selectedIllnesses.filter(it => it.name !== illness.name),
      }));
    }
    case TgvActions.TGV_TIER_ADDED: {
      if (sectionState.tiere.length >= MAX_TIERE) {
        return sectionState;
      }
      return withTiere(sectionState, [...sectionState.tiere, createTier(action.payload)]);
    }
    case TgvActions.TGV_TIER_REMOVED: {
      if (sectionState.tiere.length <= 1) {
        return sectionState;
      }
      return withTiere(
        sectionState,
        sectionState.tiere.filter(tier => tier.cardId !== action.payload),
      );
    }
    case TgvActions.TGV_VORSCHAEDEN_CHANGED: {
      const { thh, patch } = (action as TgvAction<VorschaedenChangedPayload>).payload;
      return {
        ...sectionState,
        vorschaeden: {
          ...sectionState.vorschaeden,
          [thh]: { ...sectionState.vorschaeden[thh], ...patch },
        },
      };
    }
    case TgvActions.TGV_ANGEBOTS_ID_ASSIGNED: {
      const { cardId, angebotsId } = (action as TgvAction<AngebotsIdAssignedPayload>).payload;
      return {
        ...sectionState,
        angebotsIds: { ...sectionState.angebotsIds, [cardId]: angebotsId },
      };
    }
    case TgvActions.TGV_ANGEBOTS_ID_CLEARED: {
      const { [action.payload]: _cleared, ...angebotsIds } = sectionState.angebotsIds;
      return {
        ...sectionState,
        angebotsIds,
        uebernommen: (sectionState.uebernommen ?? []).filter(key => key !== action.payload),
      };
    }
    case GlobalActions.PRODUKT_DELETED: {
      const deleted: ProduktDeleted['angebotsIds'] = action.payload?.angebotsIds ?? [];
      return withoutChildren(
        sectionState,
        new Set(deleted.map(offer => offer.angebotsId)),
        deleted.some(o => o.appId === App.THH_HUND),
        deleted.some(o => o.appId === App.THH_PFERD),
      );
    }
    case TgvActions.TGV_CHILD_LINKS_HEALED: {
      const healed: string[] = action.payload ?? [];
      return withoutChildren(
        sectionState,
        new Set(healed),
        healed.includes(App.THH_HUND),
        healed.includes(App.THH_PFERD),
      );
    }
    case TgvActions.TGV_UEBERNOMMEN:
      return {
        ...sectionState,
        uebernommen: (action.payload as string[]).filter(isListedTakeOverKey(sectionState)),
      };
    case TgvActions.TGV_ORPHANS_DEFERRED:
      return { ...sectionState, deferredDeletion: action.payload };
    case TgvActions.TGV_THH_CLEARED:
      return {
        ...sectionState,
        thhAngelegt: { ...sectionState.thhAngelegt, [action.payload]: false },
      };
    case TgvActions.TGV_THH_ANGELEGT:
      return {
        ...sectionState,
        thhAngelegt: { ...sectionState.thhAngelegt, [action.payload]: true },
      };
    case GlobalActions.EINSTIEG_REQUEST_SUCCESSFUL: {
      const versicherungsnehmer = action.payload?.versicherungsnehmer;
      if (!versicherungsnehmer) {
        return sectionState;
      }
      const adresse = versicherungsnehmer.adresse;
      return {
        ...sectionState,
        kunde: {
          plz: adresse?.plz ?? sectionState.kunde.plz,
          ort: adresse?.ort ?? sectionState.kunde.ort,
          strasse: adresse?.strasse ?? sectionState.kunde.strasse,
          hausnummer: adresse?.hausnummer ?? sectionState.kunde.hausnummer,
          geburtsdatum: versicherungsnehmer.geburtsdatum
            ? convertDateFormatFromShortIsoToLocalState(versicherungsnehmer.geburtsdatum)
            : sectionState.kunde.geburtsdatum,
        },
      };
    }
    default:
      return sectionState;
  }
};
